import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Scan, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Html5QrcodeScanner } from 'html5-qrcode';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Scanner() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [scanner, setScanner] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchZones();
    }
  }, [selectedEvent]);

  useEffect(() => {
    return () => {
      if (scanner) {
        scanner.clear();
      }
    };
  }, [scanner]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/events`);
      const published = response.data.filter(e => e.status === 'published');
      setEvents(published);
    } catch (error) {
      toast.error('Errore nel caricamento eventi');
    }
  };

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${selectedEvent}/access-zones`);
      setZones(response.data);
      if (response.data.length > 0) {
        setSelectedZone(response.data[0].id);
      }
    } catch (error) {
      toast.error('Errore nel caricamento zone');
    }
  };

  const startScanning = () => {
    if (!selectedEvent || !selectedZone) {
      toast.error('Seleziona evento e zona');
      return;
    }

    setScanning(true);
    
    const html5QrcodeScanner = new Html5QrcodeScanner(
      "qr-reader",
      { fps: 10, qrbox: { width: 250, height: 250 } },
      false
    );
    
    html5QrcodeScanner.render(onScanSuccess, onScanError);
    setScanner(html5QrcodeScanner);
  };

  const stopScanning = () => {
    if (scanner) {
      scanner.clear();
      setScanner(null);
    }
    setScanning(false);
  };

  const onScanSuccess = (decodedText) => {
    processCheckin(decodedText);
  };

  const onScanError = (error) => {
    // Silent - scanning errors are normal
  };

  const processCheckin = async (qrCode) => {
    try {
      const response = await axios.post(`${API_URL}/checkin`, {
        qr_code: qrCode,
        access_zone_id: selectedZone
      });
      
      setLastScan({
        success: true,
        message: response.data.message,
        scan_type: response.data.scan_type,
        ticket: response.data.ticket,
        ticket_type: response.data.ticket_type,
        zone: response.data.zone,
        timestamp: new Date()
      });
      
      // Success sound
      playSound('success');
      toast.success(`${response.data.scan_type === 'entry' ? 'Ingresso' : 'Uscita'} registrato: ${response.data.ticket.holder_name}`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Errore durante la scansione';
      
      setLastScan({
        success: false,
        message: errorMessage,
        timestamp: new Date()
      });
      
      // Error sound
      playSound('error');
      toast.error(errorMessage);
    }
  };

  const playSound = (type) => {
    const audio = new Audio();
    if (type === 'success') {
      audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBDGH0fPTgjMGHm7A7+OZPQ0NVq3n7q5aFQpElOH0xXAoAy178O+fSRAMUKjj8LdjHAU5kdj0zHksBChyx+/ekUAMFl+26+ytWRUKSJ/i8r1sIQQ0iNL01H45Byp6y/DfmT4NDliw6O6tWRUKSpzh9MVvJgM=';
    } else {
      audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBDGH0fPTgjMGHm7A7+OZPQ0NVq3n7q5aFQpElOH0xXAoAy178O+fSRAMUKjj8LdjHAU5kdj0zHksBChyx+/ekUAMFl+26+ytWRUKSJ/i8r1sIQQ0iNL01H45Byp6y/DfmT4NDliw6O6tWRUKSpzh9MVvJgM=';
    }
    audio.play().catch(() => {});
  };

  const handleManualCheckin = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      processCheckin(manualCode.trim());
      setManualCode('');
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="scanner-page">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Scanner QR</h1>
          <p className="text-gray-600">Scansiona i biglietti per il check-in</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Configurazione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Evento</Label>
              <Select value={selectedEvent} onValueChange={setSelectedEvent} disabled={scanning}>
                <SelectTrigger data-testid="event-select">
                  <SelectValue placeholder="Seleziona evento" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {zones.length > 0 && (
              <div>
                <Label>Zona di Accesso</Label>
                <Select value={selectedZone} onValueChange={setSelectedZone} disabled={scanning}>
                  <SelectTrigger data-testid="zone-select">
                    <SelectValue placeholder="Seleziona zona" />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map(zone => (
                      <SelectItem key={zone.id} value={zone.id}>
                        {zone.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!scanning ? (
              <Button
                onClick={startScanning}
                disabled={!selectedEvent || !selectedZone}
                className="w-full"
                data-testid="start-scanning-button"
              >
                <Scan className="w-4 h-4 mr-2" />
                Inizia Scansione
              </Button>
            ) : (
              <Button
                onClick={stopScanning}
                variant="destructive"
                className="w-full"
                data-testid="stop-scanning-button"
              >
                Ferma Scansione
              </Button>
            )}
          </CardContent>
        </Card>

        {scanning && (
          <Card>
            <CardContent className="p-6">
              <div id="qr-reader" className="w-full" data-testid="qr-reader"></div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Label htmlFor="manual-code">Inserimento Manuale Codice</Label>
                <form onSubmit={handleManualCheckin} className="flex gap-2 mt-2">
                  <Input
                    id="manual-code"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Inserisci codice QR manualmente"
                    data-testid="manual-code-input"
                  />
                  <Button type="submit" data-testid="manual-checkin-button">
                    Verifica
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        )}

        {lastScan && (
          <Card className={lastScan.success ? 'border-green-500 border-2' : 'border-red-500 border-2'}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-full ${
                  lastScan.success ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {lastScan.success ? (
                    <CheckCircle className="w-8 h-8 text-green-600" data-testid="scan-success-icon" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600" data-testid="scan-error-icon" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className={`text-2xl font-bold mb-2 ${
                    lastScan.success ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {lastScan.success ? 
                      (lastScan.scan_type === 'entry' ? 'INGRESSO REGISTRATO ✅' : 'USCITA REGISTRATA 🚪') : 
                      'ACCESSO NEGATO ❌'
                    }
                  </h3>
                  <p className="text-gray-700 mb-2">{lastScan.message}</p>
                  
                  {lastScan.success && lastScan.ticket && (
                    <div className="mt-4 space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start gap-4">
                          {lastScan.holder_photo && (
                            <div className="flex-shrink-0">
                              <img 
                                src={lastScan.holder_photo} 
                                alt="Foto intestatario"
                                className="w-24 h-24 rounded-lg object-cover border-2 border-gray-300"
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 mb-1">
                              {lastScan.ticket.holder_name}
                            </p>
                            <p className="text-sm text-gray-600">
                              Biglietto: {lastScan.ticket_type?.name}
                            </p>
                            <p className="text-sm text-gray-600">
                              Zona: {lastScan.zone?.name}
                            </p>
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-sm font-semibold text-gray-900">
                                Tipo Scan: <span className={`px-2 py-1 rounded text-xs ${
                                  lastScan.scan_type === 'entry' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {lastScan.scan_type === 'entry' ? 'ENTRATA' : 'USCITA'}
                                </span>
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {lastScan.scan_type === 'entry' ? 
                                  'L\'utente è ora all\'interno dell\'evento' : 
                                  'L\'utente è ora fuori dall\'evento'
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Storico scan */}
                      {lastScan.scan_history && lastScan.scan_history.length > 0 && (
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            📋 Storico Scansioni ({lastScan.scan_history.length})
                          </h4>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {lastScan.scan_history.map((scan, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-gray-200 last:border-0">
                                <span className={`font-semibold ${
                                  scan.scan_type === 'entry' ? 'text-green-600' : 'text-blue-600'
                                }`}>
                                  {scan.scan_type === 'entry' ? '→ ENTRATA' : '← USCITA'}
                                </span>
                                <span className="text-gray-600">
                                  {new Date(scan.scanned_at).toLocaleString('it-IT')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-2">
                    {lastScan.timestamp.toLocaleTimeString('it-IT')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
